/**
 * PTBFRollingListPage — `/purchase/contract/ptbf-rolling`.
 *
 * 1:1 Access port of `frmPurchaseMainSubFormPTBFRolling` + subform
 * `frmPurchaseQryPTBFRolling` (RecordSource view `v_ptbf_rolling_p`):
 *   - Header: New + Edit + Delete (mirrors btnNew / btnEdit / btnDelete).
 *     btnPrint + btnSubPrint exist in Access but Form_Load forces
 *     `Visible = False` unconditionally — omitted here.
 *   - Datasheet: 16 cols in Access ColumnOrder + ColumnWidth (twips).
 *   - Delete is a soft-delete (Access: update is_status = 1 where contract_no
 *     and rolling_date match), wired through useDeletePTBFRollingMutation.
 */
import { type MouseEvent, useMemo, useState } from 'react'
import {
  Box, Button, CircularProgress, Divider, Stack, Table, TableBody, TableCell, TableHead,
  TablePagination, TableRow, Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'

import BrandState from '@/components/BrandState'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '@/components/dataList'
import ConfirmDialog from '@/components/ConfirmDialog'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useDeletePTBFRollingMutation, useListPTBFRollingsQuery,
} from '@/store/api/ptbfRollingsApi'
import type { PTBFRollingOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'
import { sumNumericColumn } from '@/utils/tableTotals'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import PTBFRollingFormDialog from './PTBFRollingFormDialog'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'

type FilterKind = 'search'
const ALL_ROWS_PAGE_SIZE = 1_000_000

const actionToolbarBtnSx: SxProps<Theme> = { minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, fontSize: '0.72rem', lineHeight: 1.1, textTransform: 'none' }

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = {
  key: Extract<keyof PTBFRollingOut, string>
  label: string
  kind?: ColumnKind
  align?: 'right'
  digits?: number
  filter?: ColumnFilterKind
  /** Access ColumnWidth (twips). -2 = best fit. */
  width?: number
}
const NON_SUM_TOTAL_KEYS = new Set<string>(['spread', 'old_diffs', 'new_diffs'])

// Column order + caption + ColumnWidth (twips) per Access subform
// `frmPurchaseQryPTBFRolling` (ControlSource = column in v_ptbf_rolling_p).
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'rolling_date', label: 'Rolling Date', kind: 'date', width: 1440 },
  { key: 'contract_no', label: 'Contract no', filter: 'checkboxes', width: 1428 },
  { key: 'name', label: 'CP Name', filter: 'checkboxes', width: -2 },
  { key: 'contract_date', label: 'Contract Date', kind: 'date', width: 1620 },
  { key: 'quality_code', label: 'Quality', filter: 'checkboxes', width: 1008 },
  { key: 'lot_to_roll', label: 'Lot to roll', kind: 'number', align: 'right', digits: 0, width: 1248 },
  { key: 'quantity_kg', label: 'Kg', kind: 'number', align: 'right', digits: 0, width: 792 },
  { key: 'old_futures_month', label: 'Old Futures Month', filter: 'checkboxes', width: 2016 },
  { key: 'new_futures_month', label: 'New Futures Month', filter: 'checkboxes', width: 2124 },
  // Access Format="#,##0.00;(#,##0.00);-" + "Standard" → 2 decimals.
  // DB stores spread/rolling_fee/old_diffs là integer; new_diffs view tính
  // (old+spread-fee)::double precision nhưng input toàn integer → 2dp display
  // chỉ để match Access Standard, không phải có precision lẻ.
  { key: 'spread', label: 'Spread', kind: 'number', align: 'right', digits: 2, width: 984 },
  { key: 'rolling_fee', label: 'Rolling fee', kind: 'number', align: 'right', digits: 2, width: 1308 },
  { key: 'old_diffs', label: 'Old diffs', kind: 'number', align: 'right', digits: 2, width: 1116 },
  { key: 'new_diffs', label: 'New Diffs', kind: 'number', align: 'right', digits: 2, width: 1248 },
  { key: 'note', label: 'Remark', width: -2 },
  { key: 'updated_by', label: 'Updated Person', width: -2 },
  { key: 'updated_date', label: 'Updated Date', kind: 'datetime', width: -2 },
  { key: 'id', label: 'ID', kind: 'number', align: 'right', digits: 0, width: 576 },
  { key: 'ref_amendment', label: 'Amendment No', width: -2 },
]

/** Access twips → CSS px (1440 twips = 1 inch ≈ 96 px → 1 twip ≈ 0.0667 px). */
const accessTwipsToPx = (twips?: number): number | undefined => {
  if (twips == null || twips < 0) return undefined
  return Math.round((twips * 96) / 1440)
}

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${`${d.getFullYear()}`}` }
const formatAccessDateTime = (v: unknown): string => {
  if (!v) return ''
  const d = new Date(String(v))
  if (Number.isNaN(d.getTime())) return String(v)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}
const formatNumberFixed = (v: number, digits = 0): string => digits > 0 ? v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : (Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))

const cellValue = (row: PTBFRollingOut, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'datetime') return formatAccessDateTime(val)
  if (col.kind === 'number' && typeof val === 'number') return formatNumberFixed(val, col.digits ?? 0)
  if (typeof val === 'number') return formatNumberFixed(val, 0)
  return String(val)
}

export function PTBFRollingListPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const { selectedId, toggleSelect } = useRowSelection<number>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const { getWidth, startResize } = useColumnWidths('purchase.ptbf-rolling')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof PTBFRollingOut> | null>(null)

  const filterPopover = useFilterPopover<FilterKind>()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => filterPopover.open(kind, e)
  const closeFilterPopover = () => filterPopover.close()

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof PTBFRollingOut
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => { setSearchTerm(''); columnFilters.clearAll(); setPage(0); closeFilterPopover() }

  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<PTBFRollingOut | null>(null)
  const [confirmDel, setConfirmDel] = useState<PTBFRollingOut | null>(null)

  const { data, isLoading, isFetching, error } = useListPTBFRollingsQuery({ page: 1, page_size: ALL_ROWS_PAGE_SIZE }, { refetchOnMountOrArgChange: true })
  const [deleteRolling, { isLoading: deleting }] = useDeletePTBFRollingMutation()

  const baseRows = useMemo(() => data?.items ?? [], [data])

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'purchase',
    prefKey: 'columns',
    subKey: 'ptbf-rolling',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'purchase.ptbf-rolling',
  })
  const { visibleColumns, openMenu } = columnsCtrl

  const minTableWidth = useMemo(
    () =>
      Math.max(
        1200,
        visibleColumns.reduce((total, col) => total + (accessTwipsToPx(col.width) ?? 140), 0),
      ),
    [visibleColumns],
  )

  const filteredRows = useMemo(() =>
    baseRows.filter((row) => {
      if (!rowMatchesSearch(row as unknown as Record<string, unknown>, searchTerm, TABLE_COLUMNS as unknown as { key: string }[])) return false
      if (!rowMatchesAllColumnFilters(row as unknown as Record<string, unknown>, columnFilters.filters)) return false
      return true
    }), [baseRows, searchTerm, columnFilters.filters])

  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, visibleColumns), [filteredRows, sortState, visibleColumns])
  const rows = useMemo(() => rowsPerPage === -1 ? sortedRows : sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [page, rowsPerPage, sortedRows])

  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns.filter((col) => col.kind === 'number' && !NON_SUM_TOTAL_KEYS.has(String(col.key))).map(
          (col) => [String(col.key), sumNumericColumn(filteredRows, col.key)],
        ),
      ),
    [filteredRows, visibleColumns],
  )

  // Per-column ▽ popover metadata
  const popoverColumn = useMemo<ColumnDef | null>(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows as never) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows as unknown as Record<string, unknown>[], String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`
  const selectedRow = useMemo(() => baseRows.find((r) => r.id === selectedId) ?? null, [baseRows, selectedId])
  const toggleSort = (key: keyof PTBFRollingOut) => { setSortState((current) => toggleTableSort(current, key)); setPage(0) }

  const handleEdit = () => {
    if (!selectedRow) { snack.warning('Please choose a record!'); return }
    setEditing(selectedRow)
  }
  const handleDeleteRequest = () => {
    if (!selectedRow) { snack.warning('Please choose a record!'); return }
    setConfirmDel(selectedRow)
  }
  const handleConfirmDelete = async () => {
    if (!confirmDel) return
    try { await deleteRolling(confirmDel.id).unwrap(); snack.success('Successfully Deleted'); setConfirmDel(null) }
    catch (err) { snack.error(getErrorMessage(err, 'Failed to delete rolling')) }
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => setCreateOpen(true)}>New</Button>
            <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={handleEdit}>Edit</Button>
            <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={!canEdit || deleting} onClick={handleDeleteRequest}>Delete</Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => openFilterPopover('search', e)} />
        <Button size="small" variant="outlined" endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openMenu(e.currentTarget)} sx={filterBarBtnSx}>Columns</Button>
      </FilterBar>
      <Divider sx={{ mb: 0.5 }} />

      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>Total: {filteredRows.length}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}>Loaded: {baseRows.length}</Typography>
            <TablePagination component="div" count={filteredRows.length} page={rowsPerPage === -1 ? 0 : page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]} labelRowsPerPage="Rows" sx={{ flexShrink: 0, '& .MuiTablePagination-toolbar': { minHeight: 24, px: 0 }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': { fontSize: '0.68rem' }, '& .MuiTablePagination-actions': { transform: 'scale(0.86)', transformOrigin: 'center' } }} />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={360}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Contract no, supplier..."
              label="Search PTBF Rollings"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => { if (!colPopover) return; columnFilters.setFilter(String(colPopover.key), v); setPage(0) }}
          options={popoverOptions}
        />

        <FuturesColumnsMenu controller={columnsCtrl} />

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load PTBF Rollings"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No PTBF Rollings found"
              description="Try adjusting the table filters above."
            />
          ) : (
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {visibleColumns.map((col) => {
                  const px = accessTwipsToPx(col.width)
                  const w = getWidth(String(col.key))
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      className="app-table-header-cell"
                      onClick={() => toggleSort(col.key)}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        userSelect: 'none',
                        position: 'relative',
                        ...(!w && px ? { width: px, minWidth: px } : {}),
                      }}
                    >
                      {col.label}
                      {getTableSortSuffix(sortState, col.key)}
                      <ColumnFilterIcon
                        active={columnFilters.isActive(String(col.key))}
                        onClick={(position) => setColPopover({ key: col.key, position })}
                        ariaLabel={`Filter ${col.label}`}
                      />
                      <ResizeHandle onMouseDown={(e) => startResize(String(col.key), e)} />
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            fixedFooterContent={() => (
              <TableRow>
                {visibleColumns.map((col, index) => {
                  const total = totalRowValues.get(String(col.key))
                  const val = total == null ? '' : formatNumberFixed(total, col.digits ?? 0)
                  return (
                    <TableCell
                      key={String(col.key)}
                      align={col.align}
                      sx={{
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        py: 0.5,
                        bgcolor: 'background.paper',
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {index === 0 ? 'Total' : val}
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_: number, row: PTBFRollingOut) =>
              visibleColumns.map((col) => {
                const val = cellValue(row, col)
                const w = getWidth(String(col.key))
                return (
                  <TableCell
                    key={String(col.key)}
                    align={col.align}
                    style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                    sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    <Box component="span" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>{val || '-'}</Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const id = Number.isFinite(idx) ? rows[idx]?.id : null
                return <TableRow {...props} hover selected={id != null && id === selectedId} sx={{ cursor: 'pointer' }} onClick={() => { if (id != null) toggleSelect(id) }} />
              },
            }}
          />
          )}
        </Box>
      </Box>

      <PTBFRollingFormDialog open={createOpen} mode="create" onClose={() => setCreateOpen(false)} />
      <PTBFRollingFormDialog open={!!editing} mode="edit" initialValue={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        open={!!confirmDel}
        title="Delete PTBF Rolling"
        message={
          confirmDel
            ? `Do you want to delete Contract '${confirmDel.contract_no}' with date at '${confirmDel.rolling_date ?? ''}' ?`
            : ''
        }
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDel(null)}
      />
    </Box>
  )
}

export default PTBFRollingListPage
